// Pure helpers for the /excerpts list keyboard navigation.
// Extracted so behavior can be unit-tested without rendering the page.

export type KeyAction =
  | { type: "noop" }
  | { type: "focus"; index: number }
  | { type: "duplicate"; id: string };

export const isTypingTarget = (el: Element | null): boolean => {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
};

export interface KeyContext {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
  activeElement: Element | null;
  cards: HTMLElement[]; // ordered [data-excerpt-card] elements
}

export const decideExcerptKeyAction = (ctx: KeyContext): KeyAction => {
  if (ctx.metaKey || ctx.ctrlKey || ctx.altKey) return { type: "noop" };
  if (isTypingTarget(ctx.activeElement)) return { type: "noop" };

  const isDown = ctx.key === "j" || ctx.key === "ArrowDown";
  const isUp = ctx.key === "k" || ctx.key === "ArrowUp";
  const isDup = ctx.key === "d" || ctx.key === "D";
  if (!isDown && !isUp && !isDup) return { type: "noop" };
  if (ctx.cards.length === 0) return { type: "noop" };

  if (isDup) {
    const focused = ctx.activeElement as HTMLElement | null;
    if (!focused || !focused.matches?.("[data-excerpt-card]")) return { type: "noop" };
    const id = focused.getAttribute("data-excerpt-id");
    if (!id) return { type: "noop" };
    return { type: "duplicate", id };
  }

  const current = ctx.cards.indexOf(ctx.activeElement as HTMLElement);
  let target: number;
  if (current === -1) {
    target = isDown ? 0 : ctx.cards.length - 1;
  } else {
    target = isDown
      ? Math.min(ctx.cards.length - 1, current + 1)
      : Math.max(0, current - 1);
  }
  return { type: "focus", index: target };
};
